/**
 * RenewalTimeline Component
 *
 * Displays a visual timeline of renewal events and milestones.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate days between dates
 */
function daysBetween(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d2 - d1;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Timeline event types
 */
const EVENT_TYPES = {
  today: {
    icon: `<svg viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="8"/>
    </svg>`,
    color: 'var(--primary)',
  },
  optOut: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>`,
    color: '#ea580c',
  },
  renewal: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>`,
    color: 'var(--success)',
  },
  notification: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>`,
    color: 'var(--info)',
  },
  action: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>`,
    color: 'var(--success)',
  },
  note: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>`,
    color: 'var(--text-secondary)',
  },
};

/**
 * Create timeline events from renewal data
 */
function generateTimelineEvents(renewal) {
  const events = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Add today marker
  events.push({
    type: 'today',
    date: today.toISOString(),
    title: t('renewal.timeline.today'),
    description: '',
    isPast: false,
    isToday: true,
  });

  // Add opt-out deadline if exists
  if (renewal.optOutDeadline) {
    const optOutDate = new Date(renewal.optOutDeadline);
    events.push({
      type: 'optOut',
      date: renewal.optOutDeadline,
      title: t('renewal.timeline.optOutDeadline'),
      description: t('renewal.timeline.optOutDescription'),
      isPast: optOutDate < today,
      daysFromToday: daysBetween(today, optOutDate),
    });
  }

  // Add renewal date
  const renewalDate = new Date(renewal.renewalDate);
  events.push({
    type: 'renewal',
    date: renewal.renewalDate,
    title: t('renewal.timeline.renewalDate'),
    description: `${t('renewal.contractValue')}: ${formatCurrency(renewal.currentValue, renewal.currency)}`,
    isPast: renewalDate < today,
    daysFromToday: daysBetween(today, renewalDate),
  });

  // Add notification history
  if (renewal.notifications && renewal.notifications.length > 0) {
    renewal.notifications.forEach((notif, index) => {
      events.push({
        type: 'notification',
        date: notif.sentAt,
        title: t('renewal.timeline.notificationSent'),
        description: `${notif.daysBeforeDeadline} ${t('renewal.days')} ${t('renewal.timeline.beforeDeadline')}`,
        isPast: true,
      });
    });
  }

  // Add notes as timeline events
  if (renewal.notes && renewal.notes.length > 0) {
    renewal.notes.forEach((note) => {
      events.push({
        type: 'note',
        date: note.createdAt,
        title: note.authorName || t('renewal.timeline.note'),
        description: note.content.length > 100 ? note.content.substring(0, 100) + '...' : note.content,
        isPast: true,
      });
    });
  }

  // Sort by date
  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  return events;
}

/**
 * Format currency (uses user's preferred currency as default)
 */
function formatCurrency(amount, currency = null) {
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  const currencyCode = currency || getCurrency();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Create a renewal timeline element
 *
 * @param {Object} renewal - Renewal data
 * @param {Object} options - Timeline options
 * @returns {HTMLElement} The timeline element
 */
export function createRenewalTimeline(renewal, options = {}) {
  const {
    onEventClick = null,
    showNotes = true,
    showNotifications = true,
  } = options;

  const events = generateTimelineEvents(renewal);
  
  // Filter events based on options
  const filteredEvents = events.filter(event => {
    if (!showNotes && event.type === 'note') return false;
    if (!showNotifications && event.type === 'notification') return false;
    return true;
  });

  const container = document.createElement('div');
  container.className = 'renewal-timeline';

  // Header
  const header = document.createElement('div');
  header.className = 'renewal-timeline__header';
  header.innerHTML = `
    <h3 class="renewal-timeline__title">${t('renewal.timeline.title')}</h3>
    <div class="renewal-timeline__legend">
      <span class="renewal-timeline__legend-item">
        <span class="renewal-timeline__legend-dot" style="background: var(--primary)"></span>
        ${t('renewal.timeline.today')}
      </span>
      <span class="renewal-timeline__legend-item">
        <span class="renewal-timeline__legend-dot" style="background: #ea580c"></span>
        ${t('renewal.timeline.optOutDeadline')}
      </span>
      <span class="renewal-timeline__legend-item">
        <span class="renewal-timeline__legend-dot" style="background: var(--success)"></span>
        ${t('renewal.timeline.renewalDate')}
      </span>
    </div>
  `;
  container.appendChild(header);

  // Timeline track
  const track = document.createElement('div');
  track.className = 'renewal-timeline__track';

  filteredEvents.forEach((event, index) => {
    const eventConfig = EVENT_TYPES[event.type] || EVENT_TYPES.note;
    
    const eventEl = document.createElement('div');
    eventEl.className = `renewal-timeline__event ${event.isPast ? 'is-past' : ''} ${event.isToday ? 'is-today' : ''}`;
    
    eventEl.innerHTML = `
      <div class="renewal-timeline__event-marker" style="color: ${eventConfig.color}; border-color: ${eventConfig.color}">
        ${eventConfig.icon}
      </div>
      <div class="renewal-timeline__event-content">
        <div class="renewal-timeline__event-date">${formatDate(event.date)}</div>
        <div class="renewal-timeline__event-title">${event.title}</div>
        ${event.description ? `<div class="renewal-timeline__event-description">${event.description}</div>` : ''}
        ${event.daysFromToday !== undefined ? `
          <div class="renewal-timeline__event-days ${event.daysFromToday < 0 ? 'is-overdue' : event.daysFromToday < 14 ? 'is-urgent' : ''}">
            ${event.daysFromToday < 0 
              ? `${Math.abs(event.daysFromToday)} ${t('renewal.days')} ${t('renewal.timeline.ago')}`
              : event.daysFromToday === 0 
                ? t('renewal.timeline.today')
                : `${t('renewal.timeline.in')} ${event.daysFromToday} ${t('renewal.days')}`
            }
          </div>
        ` : ''}
      </div>
      ${index < filteredEvents.length - 1 ? '<div class="renewal-timeline__connector"></div>' : ''}
    `;

    if (onEventClick) {
      eventEl.style.cursor = 'pointer';
      eventEl.addEventListener('click', () => onEventClick(event));
    }

    track.appendChild(eventEl);
  });

  container.appendChild(track);

  return container;
}

/**
 * Create styles for renewal timeline
 */
export function injectRenewalTimelineStyles() {
  if (document.getElementById('renewal-timeline-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'renewal-timeline-styles';
  styles.textContent = `
    .renewal-timeline {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: var(--space-4);
    }

    .renewal-timeline__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--space-4);
      padding-bottom: var(--space-3);
      border-bottom: 1px solid var(--border);
    }

    .renewal-timeline__title {
      font-size: var(--text-lg);
      font-weight: 600;
      margin: 0;
    }

    .renewal-timeline__legend {
      display: flex;
      gap: var(--space-4);
    }

    .renewal-timeline__legend-item {
      display: flex;
      align-items: center;
      gap: var(--space-1);
      font-size: var(--text-xs);
      color: var(--text-secondary);
    }

    .renewal-timeline__legend-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    .renewal-timeline__track {
      position: relative;
      padding-left: var(--space-8);
    }

    .renewal-timeline__event {
      position: relative;
      padding-bottom: var(--space-4);
    }

    .renewal-timeline__event:last-child {
      padding-bottom: 0;
    }

    .renewal-timeline__event.is-past {
      opacity: 0.6;
    }

    .renewal-timeline__event.is-today {
      opacity: 1;
    }

    .renewal-timeline__event-marker {
      position: absolute;
      left: calc(-1 * var(--space-8));
      width: 32px;
      height: 32px;
      background: var(--surface);
      border: 2px solid currentColor;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }

    .renewal-timeline__event-marker svg {
      width: 16px;
      height: 16px;
    }

    .renewal-timeline__event.is-today .renewal-timeline__event-marker {
      background: var(--primary);
      border-color: var(--primary);
    }

    .renewal-timeline__event.is-today .renewal-timeline__event-marker svg {
      color: white;
    }

    .renewal-timeline__event-content {
      padding: var(--space-2) 0;
    }

    .renewal-timeline__event-date {
      font-size: var(--text-xs);
      color: var(--text-secondary);
      margin-bottom: var(--space-1);
    }

    .renewal-timeline__event-title {
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: var(--space-1);
    }

    .renewal-timeline__event-description {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .renewal-timeline__event-days {
      font-size: var(--text-xs);
      font-weight: 500;
      color: var(--text-secondary);
      margin-top: var(--space-1);
    }

    .renewal-timeline__event-days.is-urgent {
      color: #ea580c;
    }

    .renewal-timeline__event-days.is-overdue {
      color: #dc2626;
    }

    .renewal-timeline__connector {
      position: absolute;
      left: calc(-1 * var(--space-8) + 15px);
      top: 32px;
      bottom: 0;
      width: 2px;
      background: var(--border);
    }

    @media (max-width: 768px) {
      .renewal-timeline__header {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-2);
      }

      .renewal-timeline__legend {
        flex-wrap: wrap;
        gap: var(--space-2);
      }
    }
  `;

  document.head.appendChild(styles);
}

export default { createRenewalTimeline, injectRenewalTimelineStyles };
